import React, { useEffect, useLayoutEffect, useRef, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { IconStar } from '@tabler/icons-react'
import { isEmpty, isNil, trim } from 'lodash'
import classNames from 'classnames'
import { hasAgentAssistAccess } from '@/reducers/auth/currentUser.redux'

import { unauthenticateUser } from '@/reducers/auth/currentUser.actions'
import { toggleSidebarCollapsed } from '@/reducers/ui/ui.redux'
import { saveUserSetting } from '@/reducers/userSettings/userSettings.actions'

import { FAVORITES, SIDEBAR_FAVORITES } from './Sidebar.constants'
import {
  getSidebarData,
  getFavoriteLinks,
  filterSectionsBySearch,
  isSectionOpen,
} from './Sidebar.helpers'
import {
  getStoredSectionState,
  setStoredSectionState,
  getStoredSidebarCollapsed,
  setStoredSidebarCollapsed,
} from './sidebarStorage.helpers'
import { SidebarHeader } from './SidebarHeader'
import { SidebarSearch } from './SidebarSearch'
import { SidebarSection } from './SidebarSection'
import { SidebarCollapsedSection } from './SidebarCollapsedSection'
import { SidebarFooter } from './SidebarFooter'

import './Sidebar.scss'

// Module-scoped so the position survives the sidebar remounting on every
// navigation (each route wraps its own ProtectedRoute); cleared on logout so
// it can't leak into the next user's session in this tab (org switches do a
// full page load, which resets it anyway)
let savedScrollTop = null

export const SidebarComponent = ({ flags = {}, ldClient }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const currentSectionRef = useRef(null)
  const sectionsRef = useRef(null)
  const { currentOrganization, currentUser, customBranding } = useSelector((state) => state)
  const { sidebarCollapsed } = useSelector((state) => state.ui)
  const { userOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const favorites = useSelector((state) => state.userSettings.settings[SIDEBAR_FAVORITES]) ?? []
  const favoritesSaving = useSelector((state) => state.userSettings.saving)
  const isBaltoAdmin = currentUser.organizationid === 1
  const [sectionState, setSectionState] = useState(() => getStoredSectionState())
  const [searchQuery, setSearchQuery] = useState('')
  const isSearching = !isEmpty(trim(searchQuery))

  const sidebarData = useMemo(
    () => getSidebarData(currentOrganization, currentUser, flags),
    [currentOrganization, currentUser, flags]
  )
  const favoritesSection = {
    accessor: FAVORITES,
    title: 'My Favorites',
    icon: <IconStar />,
    links: getFavoriteLinks(sidebarData, favorites),
  }
  // Filter out any section entirely that has no content
  const sidebarSections = filterSectionsBySearch(
    [favoritesSection, ...sidebarData],
    searchQuery
  ).filter(({ links }) => !isEmpty(links))

  const handleToggleSidebarCollapsed = () => {
    const updatedSidebarCollapsedState = !sidebarCollapsed

    if (updatedSidebarCollapsedState) {
      setSearchQuery('')
    }

    dispatch(toggleSidebarCollapsed(updatedSidebarCollapsedState))
    setStoredSidebarCollapsed(updatedSidebarCollapsedState)
  }

  const handleToggleSection = (accessor) => {
    const updatedSectionState = {
      ...sectionState,
      [accessor]: !isSectionOpen(sectionState, accessor),
    }

    setSectionState(updatedSectionState)
    setStoredSectionState(updatedSectionState)
  }

  const handleToggleFavorite = (path) => {
    if (favoritesSaving) {
      return
    }

    const updatedFavorites = favorites.includes(path)
      ? favorites.filter((favorite) => favorite !== path)
      : [...favorites, path]

    dispatch(saveUserSetting(SIDEBAR_FAVORITES, updatedFavorites))
  }

  const handleLogout = (event) => {
    event.preventDefault()

    history.push('/login')

    // if the user is prompted to confirm and cancels, we don't want to clear
    if (history.location.pathname === '/login') {
      savedScrollTop = null
      dispatch(unauthenticateUser({ history, location, ldClient }))
    }
  }

  const handleSectionsScroll = (event) => {
    savedScrollTop = event.currentTarget.scrollTop
  }

  // Restore the exact scroll position across remounts so navigating never
  // moves the scrollbar; only a fresh page load (no saved position yet) pins
  // the active link's section so it's visible on deep links.
  useLayoutEffect(() => {
    if (!isNil(savedScrollTop) && sectionsRef.current) {
      sectionsRef.current.scrollTop = savedScrollTop
    } else if (currentSectionRef.current) {
      currentSectionRef.current.scrollIntoView({ block: 'start' })
    }

    if (sectionsRef.current) {
      savedScrollTop = sectionsRef.current.scrollTop
    }
  }, [])

  useEffect(() => {
    if (getStoredSidebarCollapsed()) {
      dispatch(toggleSidebarCollapsed(true))
    }
  }, [])

  return (
    <aside className={classNames('balto-sidebar', { collapsed: sidebarCollapsed })}>
      <SidebarHeader
        branding={customBranding}
        organizationName={currentOrganization.name}
        sidebarCollapsed={sidebarCollapsed}
        handleToggleSidebarCollapsed={handleToggleSidebarCollapsed}
      />
      {sidebarCollapsed ? (
        <nav className="balto-sidebar__collapsed-sections" aria-label="Main navigation">
          {sidebarSections.map((section) => (
            <SidebarCollapsedSection
              key={section.accessor}
              accessor={section.accessor}
              title={section.title}
              icon={section.icon}
              links={section.links}
              isFavoritesSection={section.accessor === FAVORITES}
              currentPath={location.pathname}
            />
          ))}
        </nav>
      ) : (
        <SidebarSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      )}
      {!sidebarCollapsed && (
        <div
          className="balto-sidebar__sections"
          data-testid="sidebar-sections"
          ref={sectionsRef}
          onScroll={handleSectionsScroll}
        >
          {sidebarSections.map((section) => (
            <SidebarSection
              key={section.accessor}
              accessor={section.accessor}
              title={section.title}
              links={section.links}
              isOpen={
                section.accessor === FAVORITES ||
                isSearching ||
                isSectionOpen(sectionState, section.accessor)
              }
              onToggle={handleToggleSection}
              isSearching={isSearching}
              favorites={favorites}
              favoritesSaving={favoritesSaving}
              onToggleFavorite={handleToggleFavorite}
              isFavoritesSection={section.accessor === FAVORITES}
              currentSectionRef={currentSectionRef}
              currentPath={location.pathname}
            />
          ))}
          {isSearching && isEmpty(sidebarSections) && (
            <div className="sidebar-no-results" data-testid="sidebar-no-results">
              No pages match &quot;{searchQuery}&quot;
            </div>
          )}
        </div>
      )}
      <SidebarFooter
        currentUser={currentUser}
        userOrgHierarchy={userOrgHierarchy}
        handleLogout={handleLogout}
        sidebarCollapsed={sidebarCollapsed}
        isBaltoAdmin={isBaltoAdmin}
        showAgentAssist={hasAgentAssistAccess(currentOrganization)}
        showConnectedApps={!!flags.oauthPageEnabled}
      />
    </aside>
  )
}

export const Sidebar = withLDConsumer()(SidebarComponent)
